import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Mail, MessageCircle, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EMAIL, WHATSAPP_URL, ZONE } from "@/lib/constants";
import { Reveal } from "@/components/Reveal";

const PROJECT_TYPES = ["Construction maison neuve", "Rénovation", "Extension / Surélévation", "Autre projet"];
const BUDGETS = ["< 150 000 €", "150 000 – 250 000 €", "250 000 – 400 000 €", "> 400 000 €", "À définir"];
const TIMELINES = ["Dès que possible", "3 à 6 mois", "6 à 12 mois", "Plus de 12 mois"];
const SLOTS = ["Matin (9h–12h)", "Après-midi (14h–18h)", "Soir (18h–20h)"];

const STEPS = ["Votre projet", "Vos coordonnées", "Votre message"];

const Pill = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-300 ${
      active
        ? "border-navy bg-navy text-white"
        : "border-navy/15 text-navy hover:border-cognac hover:text-cognac"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-stone-500">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none transition-colors duration-300 focus:border-cognac placeholder:text-stone-400";

const initialForm = {
  project_type: "",
  budget: "",
  surface: "",
  timeline: "",
  name: "",
  phone: "",
  email: "",
  callback_slot: "",
  message: "",
};

export const ContactSection = ({ preselectedModel }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [model, setModel] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselectedModel) setModel(preselectedModel);
  }, [preselectedModel]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext =
    step === 0
      ? form.project_type && form.budget && form.timeline
      : step === 1
      ? form.name.trim().length >= 2 && form.phone.trim().length >= 8 && /.+@.+\..+/.test(form.email)
      : true;

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/contact", { ...form, model });
      setSent(true);
      toast.success("Demande envoyée ! Nous vous rappelons très vite.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-28 sm:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac sm:text-sm">
            Parlons de votre projet
          </p>
          <h2 className="mt-4 font-display text-4xl font-medium tracking-tight text-navy sm:text-6xl">
            Racontez-nous. <span className="italic text-cognac">On vous rappelle.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-stone-600 sm:text-lg">
            Répondez à quelques questions : nous vous rappelons sous 24 à 48h
            pour échanger et prendre rendez-vous.
          </p>

          <p
            data-testid="contact-zone"
            className="mt-7 flex w-fit items-center gap-2.5 rounded-full border border-cognac/30 bg-cognac/10 px-5 py-2.5 text-sm font-bold text-navy"
          >
            <MapPin className="h-4 w-4 text-cognac" />
            Zone d'intervention : {ZONE}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="contact-email-link"
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-2.5 rounded-full border border-navy/15 px-5 py-3 text-sm font-semibold text-navy transition-colors duration-300 hover:border-cognac hover:text-cognac"
            >
              <Mail className="h-4 w-4" /> {EMAIL}
            </a>
            <a
              data-testid="contact-whatsapp-link"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-full border border-navy/15 px-5 py-3 text-sm font-semibold text-navy transition-colors duration-300 hover:border-cognac hover:text-cognac"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-navy/10 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(10,17,40,0.25)] sm:p-10">
            {sent ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-14 text-center"
              >
                <CheckCircle2 className="h-14 w-14 text-sage" />
                <h3 className="mt-6 font-display text-3xl font-medium text-navy">Merci {form.name.split(" ")[0]} !</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-stone-600 sm:text-base">
                  Votre demande est bien reçue. Nous vous rappelons au {form.phone}
                  {form.callback_slot ? ` — de préférence ${form.callback_slot.toLowerCase()}` : ""}.
                </p>
                <button
                  data-testid="contact-new-request-button"
                  onClick={() => { setSent(false); setStep(0); setForm(initialForm); setModel(""); }}
                  className="mt-8 rounded-full border border-navy/15 px-6 py-3 text-sm font-bold text-navy transition-colors duration-300 hover:border-cognac hover:text-cognac"
                >
                  Faire une autre demande
                </button>
              </motion.div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-stone-400">
                    {STEPS.map((s, i) => (
                      <span key={s} className={i === step ? "text-cognac" : i < step ? "text-navy" : ""}>
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 h-1 overflow-hidden rounded-full bg-navy/10">
                    <motion.div
                      className="h-full bg-cognac"
                      animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                </div>

                {step === 0 && (
                  <div data-testid="contact-step-0" className="space-y-7">
                    <Field label="Type de projet *">
                      <div className="flex flex-wrap gap-2.5">
                        {PROJECT_TYPES.map((t) => (
                          <Pill key={t} testid={`contact-type-${t.split(" ")[0].toLowerCase()}`} active={form.project_type === t} onClick={() => set("project_type", t)}>
                            {t}
                          </Pill>
                        ))}
                      </div>
                    </Field>
                    <Field label="Budget envisagé *">
                      <div className="flex flex-wrap gap-2.5">
                        {BUDGETS.map((b) => (
                          <Pill key={b} testid={`contact-budget-${BUDGETS.indexOf(b)}`} active={form.budget === b} onClick={() => set("budget", b)}>
                            {b}
                          </Pill>
                        ))}
                      </div>
                    </Field>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Surface (m², approx.)">
                        <input
                          data-testid="contact-surface-input"
                          className={inputCls}
                          placeholder="Ex : 120"
                          value={form.surface}
                          onChange={(e) => set("surface", e.target.value)}
                        />
                      </Field>
                      <Field label="Délai souhaité *">
                        <div className="flex flex-wrap gap-2.5">
                          {TIMELINES.map((t) => (
                            <Pill key={t} testid={`contact-timeline-${TIMELINES.indexOf(t)}`} active={form.timeline === t} onClick={() => set("timeline", t)}>
                              {t}
                            </Pill>
                          ))}
                        </div>
                      </Field>
                    </div>
                    {model && (
                      <p data-testid="contact-preselected-model" className="rounded-xl bg-sage/15 px-4 py-3 text-sm font-semibold text-navy">
                        Modèle sélectionné : <span className="text-cognac">{model}</span>
                      </p>
                    )}
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="contact-step-1" className="space-y-6">
                    <Field label="Nom et prénom *">
                      <input
                        data-testid="contact-name-input"
                        className={inputCls}
                        placeholder="Marie Dupont"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                      />
                    </Field>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Téléphone *">
                        <input
                          data-testid="contact-phone-input"
                          type="tel"
                          className={inputCls}
                          placeholder="06 12 34 56 78"
                          value={form.phone}
                          onChange={(e) => set("phone", e.target.value)}
                        />
                      </Field>
                      <Field label="Email *">
                        <input
                          data-testid="contact-email-input"
                          type="email"
                          className={inputCls}
                          placeholder="marie@exemple.fr"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                        />
                      </Field>
                    </div>
                    <Field label="Créneau préféré pour le rappel">
                      <div className="flex flex-wrap gap-2.5">
                        {SLOTS.map((s) => (
                          <Pill key={s} testid={`contact-slot-${SLOTS.indexOf(s)}`} active={form.callback_slot === s} onClick={() => set("callback_slot", s)}>
                            {s}
                          </Pill>
                        ))}
                      </div>
                    </Field>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="contact-step-2" className="space-y-6">
                    <div className="rounded-xl bg-cream p-5 text-sm leading-relaxed text-stone-600">
                      <p><strong className="text-navy">{form.project_type}</strong> · Budget {form.budget} · {form.timeline}</p>
                      <p className="mt-1">{form.name} · {form.phone} · {form.email}</p>
                      {model && <p className="mt-1">Modèle : {model}</p>}
                    </div>
                    <Field label="Votre message (facultatif)">
                      <textarea
                        data-testid="contact-message-input"
                        rows={5}
                        className={`${inputCls} resize-none`}
                        placeholder="Parlez-nous de votre terrain, de vos envies, de vos contraintes…"
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                      />
                    </Field>
                    <p className="text-xs leading-relaxed text-stone-400">
                      En envoyant ce formulaire, vous acceptez d'être recontacté par Cozyhome
                      au sujet de votre projet. Vos données ne sont jamais partagées.
                    </p>
                  </div>
                )}

                <div className="mt-9 flex items-center justify-between">
                  {step > 0 ? (
                    <button
                      data-testid="contact-back-button"
                      onClick={() => setStep(step - 1)}
                      className="flex items-center gap-2 text-sm font-bold text-stone-500 transition-colors duration-300 hover:text-navy"
                    >
                      <ArrowLeft className="h-4 w-4" /> Retour
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < 2 ? (
                    <button
                      data-testid="contact-next-button"
                      disabled={!canNext}
                      onClick={() => setStep(step + 1)}
                      className="group flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Continuer
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button
                      data-testid="contact-submit-button"
                      disabled={sending}
                      onClick={submit}
                      className="flex items-center gap-2 rounded-full bg-cognac px-8 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-navy disabled:opacity-50"
                    >
                      {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                      Envoyer ma demande
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
